import { PropsWithChildren, useCallback } from "react";
import { PlanningClassification, StatusDerived } from "../../types";
import { formatClassificationValue } from "../../utils/textFormatting";
import { ClassificationBadge } from "../ClassificationBadge";
import { StatusBadge } from "../StatusBadge";
import { CLASSIFICATION_COLS, STATUS_COLS } from "./constants";

export interface PlanningKeyStatsProps {
  classificationStats: Record<PlanningClassification, number>;
  statusStats: Record<StatusDerived, number>;
}

export function PlanningKeyStats({ classificationStats, statusStats }: PlanningKeyStatsProps) {
  const getClassificationCount = useCallback(
    (classification: PlanningClassification) => {
      if (classification === PlanningClassification.OTHER) {
        return (
          (classificationStats[PlanningClassification.OTHER] ?? 0) +
          (classificationStats[PlanningClassification.UNKNOWN] ?? 0)
        );
      }

      return classificationStats[classification] ?? 0;
    },
    [classificationStats]
  );

  return (
    <article className="flex flex-col gap-y-2">
      <h1 className="text-sm font-medium text-text-base-primary">By classification</h1>
      <KeyStatCard>
        <div className="grid grid-cols-4 gap-1">
          {CLASSIFICATION_COLS.map((classification) => (
            <KeyStatItem
              key={classification}
              heading={formatClassificationValue(classification)}
              badge={<ClassificationBadge classification={classification as PlanningClassification} />}
              value={getClassificationCount(classification)}
            />
          ))}
        </div>
      </KeyStatCard>

      <h1 className="text-sm font-medium text-text-base-primary">By status</h1>
      <KeyStatCard>
        <div className="grid grid-cols-5 gap-1">
          {STATUS_COLS.map((status) => (
            <KeyStatItem
              key={status}
              heading={status}
              value={statusStats[status]}
              badge={<StatusBadge status={status} />}
            />
          ))}
        </div>
      </KeyStatCard>
    </article>
  );
}

function KeyStatCard({ children }: PropsWithChildren) {
  return <section className="p-4 bg-white border border-border-action rounded-xl">{children}</section>;
}

interface KeyStatItemProps {
  heading: string;
  badge: JSX.Element;
  value: string | number;
}

function KeyStatItem({ heading, badge, value }: KeyStatItemProps) {
  const valueDisplay = value ?? 0;

  return (
    <section className="flex flex-col items-start cursor-default select-none">
      <h1 className="text-xs font-normal text-text-base-secondary-a">{heading}</h1>
      <div className="flex items-center justify-start gap-x-1">
        {badge}
        <span className="text-xl font-medium text-text-base-primary" title={`${heading}: ${valueDisplay}`}>
          {valueDisplay}
        </span>
      </div>
    </section>
  );
}
